import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { clipLength, clipSourceLength, songLength, fmtTime, fmtTimePrecise, frameAtMovieTime, clipFadeIn, clipFadeOut } from '../utils/movie';
import { useFrameAt } from './ClipThumb';

const LANE_H = 60;
const CLIP_LANE_H = 52; // tall enough that the frame thumbnail reads clearly
const CLIP_COLORS = ['#14e0c8', '#5b8def', '#e0a414', '#e0517a', '#9b6cf2', '#3fbf6f'];

// A clip's representative frame, drawn as the clip block's background so the
// timeline reads like a contact sheet. Which frame is shown is the clip's
// `thumbFrac` (0..1 within its trim), adjustable from the editor. Capture is
// concurrency-limited inside useClipFrame, so a long movie won't open dozens of
// decoders at once. Only mounted for blocks wide enough to be worth it.
function ClipThumbTile({ liveUrl, liveAt, fallbackUrl, preferClient }) {
  // The reliable source is the server-baked thumbnail (`fallbackUrl`) — a plain
  // image, no browser decode. When the user has picked a custom thumbFrac (after
  // the master was baked), we live-capture that exact frame from the MASTER MP4
  // (which decodes/seeks dependably, unlike long HLS clip proxies) so the change
  // shows immediately. Falls back to the baked thumb until the capture lands.
  const enabled = !!(preferClient && liveUrl);
  const { rawUrl } = useFrameAt(liveUrl, liveAt, enabled);
  const captured = enabled && rawUrl ? rawUrl : '';
  const src = captured || fallbackUrl;
  if (!src) return null;
  return (
    <img
      src={src}
      alt=""
      draggable={false}
      style={{
        position: 'absolute', inset: 0, width: '100%', height: '100%',
        objectFit: 'cover', borderRadius: 4, opacity: 1,
        pointerEvents: 'none', userSelect: 'none',
      }}
    />
  );
}

// Per-clip thumbnail picker. Scrub a slider through the clip's span; the preview
// is captured live from the composed master (reliable), and the chosen fraction
// is persisted as the clip's timeline thumbnail. Self-contained: no playhead
// dependency, so it's obvious how to change a clip's thumbnail.
function ThumbPicker({ clip, label, masterUrl, tile, onSet, onClose }) {
  const [frac, setFrac] = useState(clip?.thumbFrac == null ? 0.5 : clip.thumbFrac);
  const ts = tile?.tileStart || 0;
  const te = tile?.tileEnd != null ? tile.tileEnd : ts + 1;
  const at = ts + (te - ts) * frac;
  const { rawUrl, status } = useFrameAt(masterUrl, at, !!masterUrl);
  return (
    <div className="cs-thumbpick-backdrop" onClick={onClose}>
      <div className="cs-thumbpick" onClick={(e) => e.stopPropagation()}>
        <div className="cs-thumbpick-head">Thumbnail · {label}</div>
        <div className="cs-thumbpick-stage">
          {rawUrl ? (
            <img src={rawUrl} alt="" />
          ) : (
            <div className="cs-thumbpick-ph">
              {masterUrl ? (status === 'error' ? 'Couldn’t load frame' : 'Loading frame…') : 'Build the preview master first to pick a frame.'}
            </div>
          )}
        </div>
        <input
          type="range"
          min={0}
          max={1}
          step={0.005}
          value={frac}
          onChange={(e) => { const f = Number(e.target.value); setFrac(f); onSet(f); }}
        />
        <div className="cs-thumbpick-foot">
          <span className="cs-help">Drag to scrub this clip · frame at {Math.round(frac * 100)}%</span>
          <button type="button" className="cs-btn cs-btn-sm cs-btn-ghost" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}

// Does a clip carry an embedded audio track? Defensive across upload shapes.
function clipHasAudio(c) {
  if (!c) return false;
  if (typeof c.hasAudio === 'boolean') return c.hasAudio;
  return !!(c.audio?.hasAudio || c.metadata?.audio || c.metadata?.hasAudio || (c.audioPeaks && c.audioPeaks.length));
}

// Draws a peak array (0..1) evenly across `width` — used for a clip's own audio.
function PeakStrip({ peaks, width, height, color }) {
  const ref = useRef(null);
  useEffect(() => {
    const cv = ref.current;
    if (!cv) return;
    const w = Math.max(1, Math.min(Math.round(width), 16000));
    const dpr = window.devicePixelRatio || 1;
    cv.width = w * dpr;
    cv.height = height * dpr;
    const ctx = cv.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, height);
    const mid = height / 2;
    if (peaks && peaks.length) {
      ctx.fillStyle = color;
      for (let x = 0; x < w; x += 1) {
        const idx = Math.min(peaks.length - 1, Math.floor((x / w) * peaks.length));
        const v = peaks[idx] || 0;
        const h = Math.max(1, v * (height - 2));
        ctx.fillRect(x, mid - h / 2, 1, h);
      }
    }
  }, [peaks, width, height, color]);
  return <canvas ref={ref} style={{ width: Math.max(1, Math.round(width)), height, display: 'block' }} />;
}

// Draws a song's waveform (the [trimStart, trimEnd] slice of its peaks) plus a
// gain envelope reflecting fade in/out.
function WaveCanvas({ peaks, songDur, trimStart, trimEnd, width, height, color, fadeInSec, fadeOutSec }) {
  const ref = useRef(null);
  useEffect(() => {
    const cv = ref.current;
    if (!cv) return;
    const w = Math.max(1, Math.min(Math.round(width), 16000));
    const dpr = window.devicePixelRatio || 1;
    cv.width = w * dpr;
    cv.height = height * dpr;
    const ctx = cv.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, height);
    const mid = height / 2;
    const playLen = Math.max(0.001, (trimEnd > trimStart ? trimEnd : songDur) - trimStart);

    if (peaks && peaks.length) {
      ctx.fillStyle = color;
      for (let x = 0; x < w; x += 1) {
        const songPos = trimStart + (x / w) * playLen;
        const idx = Math.min(peaks.length - 1, Math.floor((songPos / (songDur || 1)) * peaks.length));
        const v = peaks[idx] || 0;
        const h = Math.max(1, v * (height - 4));
        ctx.fillRect(x, mid - h / 2, 1, h);
      }
    } else {
      // Not analyzed yet: flat baseline.
      ctx.strokeStyle = 'rgba(255,255,255,0.25)';
      ctx.beginPath();
      ctx.moveTo(0, mid);
      ctx.lineTo(w, mid);
      ctx.stroke();
    }

    // Gain envelope (fades).
    const pps = w / playLen;
    ctx.strokeStyle = 'rgba(255,255,255,0.8)';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(0, fadeInSec > 0 ? height - 2 : 2);
    if (fadeInSec > 0) ctx.lineTo(Math.min(w, fadeInSec * pps), 2);
    const foPx = fadeOutSec > 0 ? Math.min(w, fadeOutSec * pps) : 0;
    ctx.lineTo(w - foPx, 2);
    if (fadeOutSec > 0) ctx.lineTo(w, height - 2);
    ctx.stroke();
  }, [peaks, songDur, trimStart, trimEnd, width, height, color, fadeInSec, fadeOutSec]);
  return <canvas ref={ref} style={{ width: Math.max(1, Math.round(width)), height, display: 'block' }} />;
}

// Zoomable, multi-lane waveform timeline. Clips render as a scaled ruler; each
// song is a draggable block (move = offset) with edge handles (trim) showing
// its real waveform once analyzed. Optional live playhead.
export default function WaveTimeline({ clips, songs, movieDur, onPatchSong, playheadTime, selectedSongId, onSelectSong, onDeleteSong, selectedClipId, onSelectClip, onReorderClips, onPatchClip, clipAudio, clipThumbs, masterUrl, onSetClipThumb, onAnalyzeClip, onToggleClipAudio, onSeek, onDeselect, controlsEl }) {
  const wrapRef = useRef(null);
  const [thumbPick, setThumbPick] = useState(null);
  const [fitPx, setFitPx] = useState(20);
  const [px, setPx] = useState(0); // 0 => use fit
  const dragRef = useRef(null);
  // Clip drag-to-reorder. `clipDragRef` holds live drag bookkeeping; `clipDrag`
  // drives the on-screen drop indicator. `suppressClickRef` swallows the click
  // that fires after a real drag so it doesn't also select/seek.
  const clipDragRef = useRef(null);
  const suppressClickRef = useRef(false);
  const [clipDrag, setClipDrag] = useState(null);

  // Clip-audio lane visibility. `null` => undecided, so we fall back to "show it
  // only if some clip actually has audio" — a movie of silent clips hides the
  // lane automatically. An explicit click persists the user's choice.
  const [showClipAudioPref, setShowClipAudioPref] = useState(() => {
    try {
      const v = localStorage.getItem('cs_movie_show_clip_audio');
      if (v === '1') return true;
      if (v === '0') return false;
    } catch { /* noop */ }
    return null;
  });

  const anyClipAudio = useMemo(() => clips.some((c) => {
    const ca = (clipAudio && c.assetId) ? clipAudio[c.assetId] : null;
    if (ca) {
      if (ca.status === 'ready') return !!ca.hasAudio;
      if (ca.audioMeta === true) return true;
      if (ca.audioMeta === false) return false;
    }
    return clipHasAudio(c);
  }), [clips, clipAudio]);

  const showClipAudio = showClipAudioPref == null ? anyClipAudio : showClipAudioPref;
  const toggleClipAudioLane = () => {
    const next = !showClipAudio;
    setShowClipAudioPref(next);
    try { localStorage.setItem('cs_movie_show_clip_audio', next ? '1' : '0'); } catch { /* noop */ }
  };

  // Minimal (thin status bar) vs expanded (full waveform) clip-audio lane.
  const [clipAudioMode, setClipAudioMode] = useState(() => {
    try { return localStorage.getItem('cs_movie_clip_audio_mode') === 'wave' ? 'wave' : 'mini'; } catch { return 'mini'; }
  });
  const setClipAudioModePref = (m) => {
    setClipAudioMode(m);
    try { localStorage.setItem('cs_movie_clip_audio_mode', m); } catch { /* noop */ }
  };
  const caH = clipAudioMode === 'wave' ? 30 : 14;

  // Song lane visibility — same +/− affordance as the clip-audio lane. Default
  // shown; an explicit click persists the user's choice.
  const [showSongsPref, setShowSongsPref] = useState(() => {
    try {
      const v = localStorage.getItem('cs_movie_show_songs');
      if (v === '1') return true;
      if (v === '0') return false;
    } catch { /* noop */ }
    return null;
  });
  const showSongs = showSongsPref == null ? true : showSongsPref;
  const toggleSongsLane = () => {
    const next = !showSongs;
    setShowSongsPref(next);
    try { localStorage.setItem('cs_movie_show_songs', next ? '1' : '0'); } catch { /* noop */ }
  };

  // Master "hide all audio lanes" toggle — collapses BOTH the clip-audio and song
  // lanes so the clip thumbnails get the full height. Persisted; default shown.
  const [audioHidden, setAudioHidden] = useState(() => {
    try { return localStorage.getItem('cs_movie_hide_audio') === '1'; } catch { return false; }
  });
  const toggleAudioHidden = () => {
    setAudioHidden((prev) => {
      const next = !prev;
      try { localStorage.setItem('cs_movie_hide_audio', next ? '1' : '0'); } catch { /* noop */ }
      return next;
    });
  };
  // Taller clip lane (bigger thumbnails) when the audio lanes are hidden.
  const clipLaneH = audioHidden ? 96 : CLIP_LANE_H;

  useLayoutEffect(() => {
    const measure = () => {
      const w = wrapRef.current?.clientWidth || 800;
      setFitPx(Math.max(2, (w - 24) / Math.max(movieDur, 1)));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [movieDur]);

  const pps = px || fitPx;
  const innerW = Math.max(movieDur * pps, 1);

  const seekFromEvent = (e) => {
    if (!onSeek) return;
    const r = e.currentTarget.getBoundingClientRect();
    onSeek(Math.max(0, (e.clientX - r.left) / pps));
  };

  const clipSegs = useMemo(() => {
    let t = 0;
    return clips.map((c, i) => {
      const len = clipLength(c);
      const fps = c.fps || 30;
      const seg = {
        id: c.id,
        idx: i,
        label: c.label,
        startSec: t,
        len,
        left: t * pps,
        width: len * pps,
        color: CLIP_COLORS[i % CLIP_COLORS.length],
        frames: Math.max(1, Math.round(len * fps)),
        approxFps: !c.fps,
      };
      t += len;
      return seg;
    });
  }, [clips, pps]);

  // Live readout of which clip / frame the playhead sits on.
  const playInfo = useMemo(() => frameAtMovieTime(clips, playheadTime), [clips, playheadTime]);

  const startDrag = (e, song, type) => {
    e.stopPropagation();
    e.preventDefault();
    onSelectSong?.(song.id);
    dragRef.current = {
      id: song.id,
      type,
      startX: e.clientX,
      offset: song.movieOffset || 0,
      trimStart: song.trimStart || 0,
      trimEnd: song.trimEnd && song.trimEnd > 0 ? song.trimEnd : song.songDurationSec || 0,
      songDur: song.songDurationSec || 0,
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', endDrag);
  };
  const onMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const dt = (e.clientX - d.startX) / pps;
    // Snap window: ~10 px of drag, expressed in timeline seconds. The magnet
    // targets are the movie's end (so a trimmed song can land EXACTLY on the
    // last clip's end and the fade-out is configured from there) and t=0.
    const snapT = 10 / pps;
    if (d.type === 'move') {
      const len = Math.max(0, d.trimEnd - d.trimStart);
      let no = Math.max(0, Math.min(d.offset + dt, movieDur));
      if (Math.abs(no + len - movieDur) < snapT && movieDur - len >= 0) no = movieDur - len; // song end → movie end
      else if (Math.abs(no) < snapT) no = 0; // song start → movie start
      onPatchSong(d.id, { movieOffset: no });
    } else if (d.type === 'left') {
      const ns = Math.max(0, Math.min(d.trimStart + dt, d.trimEnd - 0.5));
      onPatchSong(d.id, { trimStart: ns });
    } else if (d.type === 'right') {
      let ne = Math.max(d.trimStart + 0.5, Math.min(d.trimEnd + dt, d.songDur || d.trimEnd + dt));
      // Snap the trimmed end to the movie's end: offset + (trimEnd - trimStart) == movieDur.
      const endTarget = d.trimStart + (movieDur - d.offset);
      if (
        endTarget > d.trimStart + 0.5 &&
        (!d.songDur || endTarget <= d.songDur) &&
        Math.abs(ne - endTarget) < snapT
      ) ne = endTarget;
      onPatchSong(d.id, { trimEnd: ne });
    }
  };
  const endDrag = () => {
    dragRef.current = null;
    window.removeEventListener('pointermove', onMove);
    window.removeEventListener('pointerup', endDrag);
  };

  // --- Clip edge trim (drag the selected clip's edges to trim OR extend) ---
  // Dragging an edge re-trims the clip against its FULL source: pulling the
  // left edge left (or the right edge right) brings trimmed footage back, up
  // to the source's bounds. Pixel deltas are timeline seconds; they map to
  // source seconds through the clip's effective speed so a 2× clip re-trims
  // 2 source seconds per timeline second.
  const clipTrimRef = useRef(null);
  const [clipTrim, setClipTrim] = useState(null); // { id, side, inSec, outSec, srcDur } while dragging
  const onClipTrimMove = (e) => {
    const d = clipTrimRef.current;
    if (!d) return;
    d.moved = true;
    const dt = ((e.clientX - d.startX) / pps) * d.spd;
    if (d.side === 'left') {
      const v = Math.max(0, Math.min(d.inSec + dt, d.outSec - 0.2));
      onPatchClip(d.id, { inSec: Math.round(v * 1000) / 1000 });
      setClipTrim({ ...d, inSec: v });
    } else {
      const cap = d.srcDur > 0 ? d.srcDur : d.outSec + Math.max(0, dt);
      const v = Math.max(d.inSec + 0.2, Math.min(d.outSec + dt, cap));
      onPatchClip(d.id, { outSec: Math.round(v * 1000) / 1000 });
      setClipTrim({ ...d, outSec: v });
    }
  };
  const endClipTrim = () => {
    const d = clipTrimRef.current;
    clipTrimRef.current = null;
    window.removeEventListener('pointermove', onClipTrimMove);
    window.removeEventListener('pointerup', endClipTrim);
    if (d && d.moved) suppressClickRef.current = true;
    setClipTrim(null);
  };
  const startClipTrim = (e, seg, side) => {
    if (!onPatchClip) return;
    e.stopPropagation();
    e.preventDefault();
    const c = clips[seg.idx];
    if (!c) return;
    const inSec = c.inSec || 0;
    const outSec = c.outSec && c.outSec > 0 ? c.outSec : c.sourceDurationSec || 0;
    clipTrimRef.current = {
      id: seg.id,
      side,
      startX: e.clientX,
      inSec,
      outSec,
      srcDur: c.sourceDurationSec || 0,
      // source seconds per timeline second (ramp-aware average)
      spd: clipSourceLength(c) / Math.max(clipLength(c), 0.001),
      moved: false,
    };
    setClipTrim({ ...clipTrimRef.current });
    window.addEventListener('pointermove', onClipTrimMove);
    window.addEventListener('pointerup', endClipTrim);
  };

  // --- Clip drag-to-reorder ---
  const onClipMove = (e) => {
    const d = clipDragRef.current;
    if (!d) return;
    if (!d.moved && Math.abs(e.clientX - d.startX) < 4) return;
    d.moved = true;
    const x = e.clientX - d.laneLeft;
    let insert = 0;
    for (const s of clipSegs) if (x > s.left + s.width / 2) insert += 1;
    d.insert = insert;
    setClipDrag({ fromIdx: d.fromIdx, insert, x });
  };
  const endClipDrag = () => {
    const d = clipDragRef.current;
    clipDragRef.current = null;
    window.removeEventListener('pointermove', onClipMove);
    window.removeEventListener('pointerup', endClipDrag);
    if (d && d.moved) {
      suppressClickRef.current = true;
      onReorderClips?.(d.fromIdx, d.insert);
    }
    setClipDrag(null);
  };
  const startClipDrag = (e, seg) => {
    if (!onReorderClips) return;
    const laneRect = e.currentTarget.parentElement.getBoundingClientRect();
    clipDragRef.current = { fromIdx: seg.idx, startX: e.clientX, laneLeft: laneRect.left, moved: false, insert: seg.idx };
    window.addEventListener('pointermove', onClipMove);
    window.addEventListener('pointerup', endClipDrag);
  };

  // Ruler ticks at a "nice" interval for the current zoom.
  const tickStep = useMemo(() => {
    const target = 80 / pps; // ~80px between ticks
    const steps = [1, 2, 5, 10, 15, 30, 60, 120, 300, 600];
    return steps.find((s) => s >= target) || 600;
  }, [pps]);
  const ticks = [];
  for (let t = 0; t <= movieDur + 0.001; t += tickStep) ticks.push(t);

  // Zoom + playhead readout cluster. When the parent provides a slot in its
  // toolbar (`controlsEl`), portal the cluster up there so the timeline doesn't
  // spend a whole row on it; otherwise render the classic inline row.
  const zoomCluster = (
    <>
      <button className="cs-tl-tbtn" title="Zoom out" onClick={() => setPx(Math.max(fitPx, (px || fitPx) / 1.6))}>−</button>
      <button className="cs-tl-tbtn" title="Fit the whole movie" onClick={() => setPx(0)}>Fit</button>
      <button className="cs-tl-tbtn" title="Zoom in" onClick={() => setPx(Math.min(120, (px || fitPx) * 1.6))}>+</button>
      <span className="cs-time" title="Timeline zoom">{pps.toFixed(1)} px/s</span>
      {playInfo && (
        <span className="cs-time" style={{ color: 'var(--text)', whiteSpace: 'nowrap' }}>
          <b style={{ color: CLIP_COLORS[playInfo.idx % CLIP_COLORS.length] }}>Clip {playInfo.idx + 1}</b>
          {' · '}frame <b>{playInfo.frame}</b>/{playInfo.totalFrames}
          {playInfo.approxFps ? ' (≈30 fps)' : ` @ ${Math.round(playInfo.fps)} fps`}
          {' · '}{fmtTimePrecise(playheadTime)}
        </span>
      )}
    </>
  );

  return (
    <div>
      {controlsEl ? (
        createPortal(zoomCluster, controlsEl)
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>{zoomCluster}</div>
      )}

      <div
        ref={wrapRef}
        onClick={(e) => { if (e.target === wrapRef.current) onDeselect?.(); }}
        style={{ overflowX: 'auto', background: '#101016', borderRadius: 8, padding: 12 }}
      >
        <div
          onClick={(e) => { if (e.target === e.currentTarget) onDeselect?.(); }}
          style={{ position: 'relative', width: innerW }}
        >
          {/* Ruler */}
          <div
            onClick={seekFromEvent}
            style={{ position: 'relative', height: 18, marginBottom: 6, cursor: onSeek ? 'pointer' : 'default' }}
          >
            {ticks.map((t) => (
              <div key={t} style={{ position: 'absolute', left: t * pps, top: 0, fontSize: 10, color: 'var(--muted)', borderLeft: '1px solid #2a2a33', paddingLeft: 3, height: 18 }}>
                {fmtTime(t)}
              </div>
            ))}
          </div>

          {/* Clip lane — each subclip is a labeled segment showing a thumbnail of
              its chosen frame (thumbFrac), with a hard boundary between clips. */}
          <div
            onClick={seekFromEvent}
            style={{ position: 'relative', height: clipLaneH, marginBottom: 8, cursor: onSeek ? 'pointer' : 'default' }}
          >
            {clipSegs.map((s) => {
              const active = playInfo && playInfo.idx === s.idx;
              const selected = selectedClipId === s.id;
              const ring = selected
                ? '0 0 0 2px #14e0c8, 0 0 10px rgba(20,224,200,0.55)'
                : active
                ? '0 0 0 2px rgba(255,255,255,0.85)'
                : 'none';
              const c = clips[s.idx] || {};
              const frac = Math.max(0, Math.min(1, c.thumbFrac == null ? 0.5 : c.thumbFrac));
              const showThumb = s.width > 36;
              return (
                <div
                  key={s.id}
                  className="cs-tl-clip"
                  title={`Clip ${s.idx + 1}: ${s.label} · ${fmtTime(s.len)} · ${s.frames} frames${s.approxFps ? ' (≈30 fps)' : ''}\nClick to select${onReorderClips ? ' · drag to reorder' : ''}`}
                  onPointerDown={(e) => startClipDrag(e, s)}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (suppressClickRef.current) { suppressClickRef.current = false; return; }
                    onSelectClip?.(s.id);
                    if (onSeek) {
                      const r = e.currentTarget.parentElement.getBoundingClientRect();
                      onSeek(Math.max(0, (e.clientX - r.left) / pps));
                    }
                  }}
                  style={{
                    position: 'absolute', left: s.left, width: Math.max(s.width - 2, 2), top: 0, height: clipLaneH,
                    background: '#15151c',
                    opacity: clipDrag && clipDrag.fromIdx === s.idx ? 0.4 : 1,
                    borderRadius: 4, boxShadow: ring, zIndex: selected ? 2 : 1,
                    display: 'flex', alignItems: 'flex-end', gap: 5, overflow: 'hidden',
                    whiteSpace: 'nowrap', padding: '0 6px 3px', boxSizing: 'border-box',
                    cursor: onReorderClips ? 'grab' : onSelectClip ? 'pointer' : 'default',
                    touchAction: 'none', userSelect: 'none',
                  }}
                >
                  {showThumb && (() => {
                    const entry = clipThumbs && clipThumbs[c.id];
                    const ts = entry?.tileStart || 0;
                    const te = entry?.tileEnd != null ? entry.tileEnd : ts + 1;
                    return (
                      <ClipThumbTile
                        preferClient={c.thumbFrac != null}
                        liveUrl={masterUrl}
                        liveAt={ts + (te - ts) * frac}
                        fallbackUrl={entry ? entry.rawUrl : ''}
                      />
                    );
                  })()}
                  {/* Per-clip fade indicators: a black/white gradient wedge at
                      the edge the fade covers, scaled to its duration. */}
                  {(() => {
                    const fin = clipFadeIn(c);
                    const fout = clipFadeOut(c);
                    if (!fin && !fout) return null;
                    const rgb = (f) => (f.color === 'white' ? '255,255,255' : '0,0,0');
                    const wFor = (f) => Math.max(4, Math.min(f.sec * pps, s.width / 2));
                    return (
                      <>
                        {fin && (
                          <div
                            title={`Fades in from ${fin.color} over ${fin.sec}s`}
                            style={{
                              position: 'absolute', left: 0, top: 0, bottom: 0, width: wFor(fin),
                              background: `linear-gradient(90deg, rgba(${rgb(fin)},0.85), rgba(${rgb(fin)},0))`,
                              pointerEvents: 'none', zIndex: 1, borderRadius: '4px 0 0 4px',
                            }}
                          />
                        )}
                        {fout && (
                          <div
                            title={`Fades out to ${fout.color} over ${fout.sec}s`}
                            style={{
                              position: 'absolute', right: 0, top: 0, bottom: 0, width: wFor(fout),
                              background: `linear-gradient(270deg, rgba(${rgb(fout)},0.85), rgba(${rgb(fout)},0))`,
                              pointerEvents: 'none', zIndex: 1, borderRadius: '0 4px 4px 0',
                            }}
                          />
                        )}
                      </>
                    );
                  })()}
                  {/* Trim/extend handles — selected clip only. Drag an edge to
                      re-trim against the full source: outward brings trimmed
                      footage back, inward cuts more. */}
                  {selected && onPatchClip && (() => {
                    const inSec = c.inSec || 0;
                    const outSec = c.outSec && c.outSec > 0 ? c.outSec : c.sourceDurationSec || 0;
                    const srcDur = c.sourceDurationSec || 0;
                    const headL = inSec; // unused source before the in-point
                    const headR = srcDur > 0 ? Math.max(0, srcDur - outSec) : 0; // after the out-point
                    const hStyle = {
                      position: 'absolute', top: 0, bottom: 0, width: 10, zIndex: 4,
                      cursor: 'ew-resize', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: 'rgba(20,224,200,0.55)', touchAction: 'none',
                    };
                    return (
                      <>
                        <div
                          onPointerDown={(e) => startClipTrim(e, s, 'left')}
                          title={`Drag to re-trim the clip's start.\n← extend: ${fmtTime(headL)} of trimmed source available before this point\n→ trim more`}
                          style={{ ...hStyle, left: 0, borderRadius: '4px 0 0 4px' }}
                        >
                          <span style={{ width: 2, height: '46%', background: 'rgba(255,255,255,0.9)', borderRadius: 1 }} />
                        </div>
                        <div
                          onPointerDown={(e) => startClipTrim(e, s, 'right')}
                          title={`Drag to re-trim the clip's end.\n→ extend: ${srcDur > 0 ? `${fmtTime(headR)} of trimmed source available after this point` : 'source length unknown'}\n← trim more`}
                          style={{ ...hStyle, right: 0, borderRadius: '0 4px 4px 0' }}
                        >
                          <span style={{ width: 2, height: '46%', background: 'rgba(255,255,255,0.9)', borderRadius: 1 }} />
                        </div>
                      </>
                    );
                  })()}
                  {/* Live readout while re-trimming */}
                  {clipTrim && clipTrim.id === s.id && (
                    <span
                      style={{
                        position: 'absolute', top: 2, left: '50%', transform: 'translateX(-50%)',
                        zIndex: 5, fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap',
                        background: 'rgba(0,0,0,0.75)', color: '#14e0c8', borderRadius: 4, padding: '1px 6px',
                        pointerEvents: 'none',
                      }}
                    >
                      {fmtTime(clipTrim.inSec)} → {fmtTime(clipTrim.outSec)}
                      {clipTrim.srcDur > 0 ? ` of ${fmtTime(clipTrim.srcDur)}` : ''}
                    </span>
                  )}
                  {/* Per-clip thumbnail picker handle (top-right). */}
                  {showThumb && onSetClipThumb && (
                    <button
                      type="button"
                      className="cs-tl-thumbedit"
                      title="Choose this clip's timeline thumbnail"
                      aria-label="Choose this clip's timeline thumbnail"
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => { e.stopPropagation(); setThumbPick(s.id); }}
                    >
                      🖼
                    </button>
                  )}
                  {/* Bottom scrim so the index/label stay legible over a photo. */}
                  <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 18, background: 'linear-gradient(transparent, rgba(0,0,0,0.6))', pointerEvents: 'none', borderRadius: '0 0 4px 4px' }} />
                  <span
                    style={{
                      position: 'relative', zIndex: 1,
                      flex: '0 0 auto', minWidth: 15, height: 15, borderRadius: 8, padding: '0 5px',
                      background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 10, fontWeight: 800,
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    {s.idx + 1}
                  </span>
                  {s.width > 44 && (
                    <span style={{ position: 'relative', zIndex: 1, fontSize: 10, color: '#fff', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', textShadow: '0 1px 2px rgba(0,0,0,0.9)' }}>
                      {s.label}
                    </span>
                  )}
                </div>
              );
            })}
            {/* Boundary ticks between clips — teal + ⇄ when the cut is a
                cross-dissolve (previous clip's dissolveSec > 0). */}
            {clipSegs.slice(1).map((s) => {
              const d = (clips[s.idx - 1] || {}).dissolveSec || 0;
              return (
                <div
                  key={`bnd-${s.id}`}
                  title={d > 0 ? `Cross-dissolve ${d}s across this cut (baked on Publish)` : undefined}
                  style={{ position: 'absolute', left: s.left, top: -2, bottom: -2, width: 2, background: d > 0 ? '#14e0c8' : '#0b0b10', zIndex: 3 }}
                >
                  {d > 0 && (
                    <span
                      style={{
                        position: 'absolute', top: -2, left: -7, fontSize: 9, fontWeight: 700,
                        color: '#04201c', background: '#14e0c8', borderRadius: 3, padding: '0 2px',
                        pointerEvents: 'none',
                      }}
                    >
                      ⇄
                    </span>
                  )}
                </div>
              );
            })}
            {/* Drop indicator while reordering */}
            {clipDrag && (
              <div
                style={{
                  position: 'absolute',
                  left: (clipSegs[clipDrag.insert] ? clipSegs[clipDrag.insert].left : innerW) - 1,
                  top: -3, bottom: -3, width: 3, borderRadius: 2,
                  background: '#14e0c8', boxShadow: '0 0 8px rgba(20,224,200,0.8)', pointerEvents: 'none', zIndex: 5,
                }}
              />
            )}
          </div>

          {/* Clip-audio controls — a tiny +/− to expand/collapse the audio lane,
              right where it appears. Mode toggle shows only once expanded. Hidden
              entirely when the master "Hide audio" toggle is on. */}
          {/* Master audio visibility — a tiny −/+ pinned at the audio section
              itself (right edge of the visible area), replacing the old
              full-size "Hide audio" toolbar button. */}
          {audioHidden && (
            <div className="cs-tl-audhdr cs-tl-audmaster">
              <button
                type="button"
                className="cs-tl-audtoggle"
                onClick={toggleAudioHidden}
                title="Show the audio lanes (clip audio + songs)"
                aria-label="Show audio lanes"
              >
                +
              </button>
              <span className="cs-tl-audlabel">audio</span>
            </div>
          )}
          {!audioHidden && (
          <div className="cs-tl-audhdr">
            <button
              type="button"
              className={`cs-tl-audtoggle${showClipAudio ? ' is-on' : ''}`}
              onClick={toggleClipAudioLane}
              title={showClipAudio ? 'Hide clip audio' : 'Show clip audio'}
              aria-label={showClipAudio ? 'Hide clip audio' : 'Show clip audio'}
            >
              {showClipAudio ? '−' : '+'}
            </button>
            <span className="cs-tl-audlabel">Clip audio</span>
            {showClipAudio && (
              <div className="cs-segment cs-segment-sm cs-tl-audmode" role="group" aria-label="Clip audio view">
                <button className={clipAudioMode === 'mini' ? 'on' : ''} onClick={() => setClipAudioModePref('mini')} title="Slim status bar">Minimal</button>
                <button className={clipAudioMode === 'wave' ? 'on' : ''} onClick={() => setClipAudioModePref('wave')} title="Show each clip's waveform">Waveform</button>
              </div>
            )}
            <span className="cs-tl-audhide">
              <button
                type="button"
                className="cs-tl-audtoggle"
                onClick={toggleAudioHidden}
                title="Hide ALL audio lanes (clip audio + songs) for bigger clip thumbnails"
                aria-label="Hide all audio lanes"
              >
                −
              </button>
            </span>
          </div>
          )}

          {/* Clip-audio lane — each clip's own embedded audio, attached under its
              video block (iMovie style). Minimal mode shows a slim status bar;
              Waveform mode draws each clip's analyzed peaks + analyze controls. */}
          {!audioHidden && showClipAudio && (
          <div
            onClick={seekFromEvent}
            style={{ position: 'relative', height: caH, marginBottom: 10, cursor: onSeek ? 'pointer' : 'default' }}
          >
            {clipSegs.map((s) => {
              const c = clips[s.idx] || {};
              const ca = (clipAudio && c.assetId) ? clipAudio[c.assetId] : null;
              const status = ca?.status || 'none';
              const analyzing = status === 'queued' || status === 'analyzing';
              const ready = status === 'ready';
              const peaks = (ca && ca.peaks && ca.peaks.length) ? ca.peaks : c.audioPeaks;
              // Resolve the audio state. When ready, the worker's answer is final.
              // Before that we only "know" there's no audio if ffprobe said so
              // (audioMeta === false); otherwise it's not-yet-analyzed.
              const knownNoAudio = ready ? !ca.hasAudio : (ca ? ca.audioMeta === false : false);
              const hasAudio = ready
                ? !!ca.hasAudio
                : (peaks && peaks.length ? true : (ca ? ca.audioMeta === true : clipHasAudio(c)));
              const muted = !!c.audioMuted;
              const live = hasAudio && !muted;
              const w = Math.max(s.width - 2, 2);
              const mini = clipAudioMode !== 'wave';
              // Offer analysis when it hasn't run, isn't running, and we don't
              // already know the clip is silent.
              const canAnalyze = !!onAnalyzeClip && !!c.assetId && !analyzing && !ready && !knownNoAudio;
              return (
                <div
                  key={`ca-${s.id}`}
                  title={analyzing
                    ? `Clip ${s.idx + 1} · analyzing audio…`
                    : knownNoAudio
                      ? `Clip ${s.idx + 1} · no audio track`
                      : ready && hasAudio
                        ? `Clip ${s.idx + 1} audio${muted ? ' · muted' : ''} — click the speaker to ${muted ? 'unmute' : 'mute'}`
                        : `Clip ${s.idx + 1} · audio not analyzed${mini ? '' : ' — click Analyze'}`}
                  style={{
                    position: 'absolute', left: s.left, width: w, top: 0, height: caH,
                    background: live ? 'rgba(20,224,200,0.10)' : 'rgba(255,255,255,0.035)',
                    border: `1px solid ${live ? 'rgba(20,224,200,0.45)' : 'rgba(255,255,255,0.12)'}`,
                    borderRadius: 4, boxSizing: 'border-box', overflow: 'hidden',
                    display: 'flex', alignItems: 'center', opacity: knownNoAudio ? 0.7 : 1,
                  }}
                >
                  {mini ? (
                    // Minimal: a slim status fill — solid teal when audible, faint
                    // otherwise. No waveform/analyze chrome to keep it unobtrusive.
                    <div style={{
                      width: '100%', height: '100%',
                      background: live
                        ? 'repeating-linear-gradient(90deg, rgba(20,224,200,0.65) 0 2px, rgba(20,224,200,0.25) 2px 4px)'
                        : 'transparent',
                      pointerEvents: 'none',
                    }}
                    />
                  ) : ready && hasAudio && peaks && peaks.length ? (
                    <PeakStrip
                      peaks={peaks}
                      width={w}
                      height={caH}
                      color={live ? 'rgba(20,224,200,0.85)' : 'rgba(255,255,255,0.25)'}
                    />
                  ) : canAnalyze ? (
                    <button
                      type="button"
                      className="cs-ca-analyze"
                      title="Extract this clip's audio waveform (on-demand)"
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => { e.stopPropagation(); onAnalyzeClip(c.assetId); }}
                    >
                      🌊 Analyze
                    </button>
                  ) : (
                    <span style={{
                      fontSize: 10, color: 'var(--muted)', padding: '0 8px', whiteSpace: 'nowrap',
                      overflow: 'hidden', textOverflow: 'ellipsis', pointerEvents: 'none',
                    }}
                    >
                      {analyzing ? '♪ analyzing…' : '🔇 no audio'}
                    </span>
                  )}
                  {ready && hasAudio && onToggleClipAudio && (
                    <button
                      type="button"
                      title={muted ? 'Unmute this clip’s audio' : 'Mute this clip’s audio'}
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => { e.stopPropagation(); onToggleClipAudio(s.id); }}
                      style={{
                        position: 'absolute', top: '50%', right: 3, transform: 'translateY(-50%)',
                        width: mini ? 14 : 18, height: mini ? 14 : 18, lineHeight: mini ? '12px' : '16px', padding: 0,
                        borderRadius: 9, border: 'none', cursor: 'pointer', zIndex: 3,
                        background: 'rgba(0,0,0,0.5)', color: '#fff', fontSize: mini ? 9 : 11,
                      }}
                    >
                      {muted ? '🔇' : '🔊'}
                    </button>
                  )}
                  {!mini && ready && onAnalyzeClip && c.assetId && (
                    <button
                      type="button"
                      title="Re-analyze this clip's audio"
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => { e.stopPropagation(); onAnalyzeClip(c.assetId); }}
                      style={{
                        position: 'absolute', top: '50%', left: 4, transform: 'translateY(-50%)',
                        width: 16, height: 16, lineHeight: '14px', padding: 0, borderRadius: 8,
                        border: 'none', cursor: 'pointer', zIndex: 3,
                        background: 'rgba(0,0,0,0.4)', color: '#fff', fontSize: 9,
                      }}
                    >
                      ↻
                    </button>
                  )}
                </div>
              );
            })}
            {/* Boundary ticks between clips, mirroring the video lane */}
            {clipSegs.slice(1).map((s) => (
              <div key={`cabnd-${s.id}`} style={{ position: 'absolute', left: s.left, top: -2, bottom: -2, width: 2, background: '#0b0b10' }} />
            ))}
          </div>
          )}

          {/* Song-lane controls — matching +/− to expand/collapse the music lanes. */}
          {!audioHidden && songs.length > 0 && (
            <div className="cs-tl-audhdr">
              <button
                type="button"
                className={`cs-tl-audtoggle${showSongs ? ' is-on' : ''}`}
                onClick={toggleSongsLane}
                title={showSongs ? 'Hide songs' : 'Show songs'}
                aria-label={showSongs ? 'Hide songs' : 'Show songs'}
              >
                {showSongs ? '−' : '+'}
              </button>
              <span className="cs-tl-audlabel">Songs{songs.length ? ` (${songs.length})` : ''}</span>
            </div>
          )}

          {/* Song lanes */}
          {!audioHidden && showSongs && songs.map((s) => {
            const offset = s.movieOffset || 0;
            const len = songLength(s, movieDur);
            const trimStart = s.trimStart || 0;
            const trimEnd = s.trimEnd && s.trimEnd > 0 ? s.trimEnd : s.songDurationSec || 0;
            const left = offset * pps;
            const width = Math.max(len * pps, 6);
            const sel = selectedSongId === s.id;
            const analyzing = s.waveform?.status === 'queued' || s.waveform?.status === 'analyzing';
            const hw = sel ? 12 : 9; // trim-handle width — fatter (and lit) when selected
            return (
              <div
                key={s.id}
                onClick={(e) => { if (e.target === e.currentTarget) onDeselect?.(); }}
                style={{ position: 'relative', height: LANE_H, marginBottom: 6 }}
              >
                <div
                  onPointerDown={(e) => startDrag(e, s, 'move')}
                  title={`♪ ${s.label}\nClick to select · drag to move · drag the edges to trim`}
                  style={{
                    position: 'absolute', left, width, top: 0, height: LANE_H,
                    background: sel ? 'rgba(20,224,200,0.16)' : 'rgba(229,9,20,0.10)',
                    border: `${sel ? 2 : 1}px solid ${sel ? 'var(--surf)' : 'rgba(229,9,20,0.6)'}`,
                    boxShadow: sel ? '0 0 0 1px rgba(20,224,200,0.35), 0 0 12px rgba(20,224,200,0.45)' : 'none',
                    borderRadius: 6, cursor: 'grab', overflow: 'hidden', touchAction: 'none', userSelect: 'none',
                    zIndex: sel ? 2 : 1,
                  }}
                >
                  <div style={{ position: 'absolute', top: 2, left: 6, right: sel ? 22 : 6, fontSize: 11, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', pointerEvents: 'none', zIndex: 2, textShadow: '0 1px 2px #000' }}>
                    ♪ {s.label}{s.loop ? ' · ⟳ loops' : ''}{analyzing ? ' · analyzing…' : !(s.peaks && s.peaks.length) ? ' · no waveform' : ''}
                  </div>
                  <div style={{ position: 'absolute', top: 16, left: 0, right: 0, bottom: 0 }}>
                    <WaveCanvas
                      peaks={s.peaks} songDur={s.songDurationSec || trimEnd || 1}
                      trimStart={trimStart} trimEnd={trimEnd}
                      width={width} height={LANE_H - 16}
                      color={sel ? '#14e0c8' : 'rgba(255,120,130,0.9)'}
                      fadeInSec={s.fadeInSec || 0} fadeOutSec={s.fadeOutSec || 0}
                    />
                  </div>
                  {/* trim handles — grab the edges to set in/out */}
                  <div
                    onPointerDown={(e) => startDrag(e, s, 'left')}
                    title="Drag to trim the song's start"
                    style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: hw, cursor: 'ew-resize', background: sel ? 'rgba(20,224,200,0.55)' : 'rgba(255,255,255,0.25)', zIndex: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    <span style={{ width: 2, height: '46%', background: 'rgba(255,255,255,0.85)', borderRadius: 1 }} />
                  </div>
                  <div
                    onPointerDown={(e) => startDrag(e, s, 'right')}
                    title="Drag to trim the song's end"
                    style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: hw, cursor: 'ew-resize', background: sel ? 'rgba(20,224,200,0.55)' : 'rgba(255,255,255,0.25)', zIndex: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    <span style={{ width: 2, height: '46%', background: 'rgba(255,255,255,0.85)', borderRadius: 1 }} />
                  </div>
                  {/* delete (only on the selected song, so it never clutters) */}
                  {sel && onDeleteSong && (
                    <button
                      type="button"
                      title="Remove this song (Delete)"
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => { e.stopPropagation(); onDeleteSong(s.id); }}
                      style={{
                        position: 'absolute', top: 2, right: 3, width: 16, height: 16, lineHeight: '14px',
                        padding: 0, borderRadius: 8, border: 'none', cursor: 'pointer', zIndex: 4,
                        background: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: 11, fontWeight: 700,
                      }}
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          {/* Playhead */}
          {playheadTime != null && playheadTime >= 0 && (
            <div style={{ position: 'absolute', top: 0, bottom: 0, left: playheadTime * pps, width: 2, background: '#fff', pointerEvents: 'none' }}>
              {playInfo && (
                <span
                  style={{
                    position: 'absolute', top: -2, left: 3, fontSize: 9, fontWeight: 700, color: '#04201c',
                    background: '#fff', borderRadius: 3, padding: '1px 4px', whiteSpace: 'nowrap',
                  }}
                >
                  f{playInfo.frame}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
      {thumbPick && (() => {
        const c = clips.find((x) => x.id === thumbPick);
        if (!c) return null;
        const idx = clips.indexOf(c);
        return (
          <ThumbPicker
            clip={c}
            label={`Clip ${idx + 1}`}
            masterUrl={masterUrl}
            tile={clipThumbs && clipThumbs[c.id]}
            onSet={(f) => onSetClipThumb && onSetClipThumb(c.id, f)}
            onClose={() => setThumbPick(null)}
          />
        );
      })()}
    </div>
  );
}
